import { computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  fromUnixTime,
  isToday,
  isTomorrow,
  isSameYear,
  isThisWeek,
} from 'date-fns';

const parseDueDate = task => {
  const dueAt = task?.dueAt;
  return dueAt ? fromUnixTime(dueAt) : null;
};

export const isTaskOverdue = task => {
  const date = parseDueDate(task);
  if (!date || task?.status === 'done') return false;
  return date.getTime() < Date.now();
};

// Which due-date group a task belongs to in the grouped list view.
// Independent of `status` (workflow stage) — a task can be "done" and
// still resolve to its original due bucket, though the grouped list only
// shows open tasks in practice.
export const getTaskDueBucket = task => {
  const date = parseDueDate(task);
  if (!date) return 'no_date';
  if (isTaskOverdue(task)) return 'overdue';
  if (isToday(date)) return 'today';
  if (isThisWeek(date, { weekStartsOn: 1 })) return 'this_week';
  return 'later';
};

export const isTaskDueToday = task => {
  const date = parseDueDate(task);
  return !!date && isToday(date) && !isTaskOverdue(task);
};

const time = date =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Same label as `useTaskDueDate`, for callers that format many tasks at once
 * (the grouped list) and therefore cannot call a composable per row.
 *
 * @param {Object} task - Task with `dueAt` (unix seconds) and `status`.
 * @param {Function} t - vue-i18n translate function.
 */
export const formatTaskDueLabel = (task, t) => {
  const date = parseDueDate(task);
  if (!date) return '';

  if (isToday(date)) {
    return `${t('TASKS_DASHBOARD.DUE.TODAY')} · ${time(date)}`;
  }
  if (isTomorrow(date)) {
    return `${t('TASKS_DASHBOARD.DUE.TOMORROW')} · ${time(date)}`;
  }

  const day = date.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    ...(isSameYear(date, new Date()) ? {} : { year: 'numeric' }),
  });
  return `${day} · ${time(date)}`;
};

/**
 * Formats a task due date into a short, friendly label ("Hoy · 11:10",
 * "Mañana · 09:00", "31 ago · 11:10") and reports whether it is overdue.
 *
 * @param {import('vue').Ref<Object>|Object} task - Task with `dueAt` (unix seconds) and `status`.
 */
export function useTaskDueDate(task) {
  const { t } = useI18n();

  const dueDate = computed(() => parseDueDate(unref(task)));

  const label = computed(() => formatTaskDueLabel(unref(task), t));

  const isOverdue = computed(() => isTaskOverdue(unref(task)));

  const isDueToday = computed(() => isTaskDueToday(unref(task)));

  const dueBucket = computed(() => getTaskDueBucket(unref(task)));

  return { dueDate, label, isOverdue, isDueToday, dueBucket };
}
